"use client";

// Catches the front-page faults a visitor would hit but no one would hear
// about, and reports them to /api/log-error for /admin/errors:
//
//   · uncaught JavaScript errors      (window "error")
//   · unhandled promise rejections    (window "unhandledrejection")
//   · assets that fail to load        (capture-phase "error" on img/script/css)
//
// The asset case is the one that bites this site in particular: a broken
// image or a chunk that 404s after a deploy renders as a blank space, with
// nothing in any log. Mounted once in the root layout, alongside SiteTracker.
import { useEffect } from "react";
import { reportError } from "@/lib/reportError";

export function ErrorLogger() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      // Cross-origin scripts report as a bare "Script error." with no detail;
      // logging those adds noise no one can act on.
      if (!e.message || e.message === "Script error.") return;
      reportError({
        kind: "client",
        message: e.message,
        source: e.filename ? `${e.filename}:${e.lineno ?? 0}:${e.colno ?? 0}` : null,
        stack: e.error?.stack ?? null,
      });
    };

    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason;
      const message =
        r instanceof Error ? r.message : typeof r === "string" ? r : "Unhandled promise rejection";
      reportError({
        kind: "promise",
        message,
        stack: r instanceof Error ? r.stack ?? null : null,
      });
    };

    // Asset failures do not bubble, so this listens in the capture phase.
    const onResource = (e: Event) => {
      const el = e.target as HTMLElement | null;
      if (!el || el === (window as unknown as HTMLElement)) return;
      const tag = el.tagName?.toLowerCase();
      if (tag !== "img" && tag !== "script" && tag !== "link" && tag !== "video") return;
      const url =
        (el as HTMLImageElement).currentSrc ||
        (el as HTMLImageElement).src ||
        (el as HTMLLinkElement).href ||
        "";
      if (!url) return;
      reportError({
        kind: "resource",
        message: `Failed to load ${tag}`,
        source: url,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("error", onResource, true);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("error", onResource, true);
    };
  }, []);

  return null;
}
